import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  FaEdit,
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaDollarSign,
  FaCreditCard,
  FaCheck,
} from "react-icons/fa";
import { getUserProfile } from "../../Redux/userSlice";
import countries from '../../data/countries.json';
import currencyData from '../../data/currency.json';
import axios from '../../services/axiosConfig';
import { INFLUENCER_BASE } from "../constants";
import useInstagramInsightsSync from "../hooks/useInstagramInsightsSync";
import InfluencerSubscriptionBilling from "../../modules/influencer-subscription/components/InfluencerSubscriptionBilling";

const InfluancerProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: user, loading } = useSelector((state) => state.user);
  const [selectedImage, setSelectedImage] = useState(null);
  const [servicePayment, setServicePayment] = useState(null);
  const [loadingServicePayment, setLoadingServicePayment] = useState(true);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);
  const [profileImageCandidateIndex, setProfileImageCandidateIndex] = useState(0);
  const { syncing, syncInstagramInsights } = useInstagramInsightsSync();


  useEffect(() => {
    if (user) {
      fetchServicePayment();
    }
  }, [user]);


  useEffect(() => {
    if (!hasFetchedInitial) {
      dispatch(getUserProfile());
      setHasFetchedInitial(true);
    }
  }, [dispatch, hasFetchedInitial]);

  useEffect(() => {
    if (!user && hasFetchedInitial) {
      console.log("No user found. Maybe token expired?");
    }
  }, [user, hasFetchedInitial]);


  const refreshInsights = async () => {
    const result = await syncInstagramInsights();
    if (!result.success) {
      alert(result.message);
    }
  };


  const fetchServicePayment = async () => {
    try {
      setLoadingServicePayment(true);
      const response = await axios.get(`/auth/users/service-payment/${user._id}`, {
        withCredentials: true
      });

      if (response.data.success) {
        setServicePayment(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching service payment:', error);
      // Service payment not found is not an error for display purposes
    } finally {
      setLoadingServicePayment(false);
    }
  };

  const getCurrentCurrencySymbol = (platform) => {
    if (!servicePayment || !servicePayment[platform]?.currency) return '';
    const country = currencyData.countries.find(c => c.code === servicePayment[platform].currency);
    return country ? country.symbol : '';
  };

  const getImageCandidates = (candidates) => {
    const uniqueCandidates = new Set();
    candidates.forEach((candidate) => {
      if (typeof candidate === "string" && candidate.trim().length > 0) {
        uniqueCandidates.add(candidate);
      }
    });
    return Array.from(uniqueCandidates);
  };

  const profileImageCandidates = useMemo(() => getImageCandidates([
    user?.profilePictureUrl,
    user?.profileImage?.url,
    user?.instagramProfilePicture,
    user?.social?.instagram?.profilePictureUrl,
    user?.social?.instagram?.profileImage?.url,
  ]), [
    user?.profilePictureUrl,
    user?.profileImage?.url,
    user?.instagramProfilePicture,
    user?.social?.instagram?.profilePictureUrl,
    user?.social?.instagram?.profileImage?.url,
  ]);

  const activeProfileImageUrl = profileImageCandidates[profileImageCandidateIndex];

  const getInitial = (username) => {
    if (activeProfileImageUrl) {
      return (
        <img
          src={activeProfileImageUrl}
          alt="Profile"
          className="w-full h-full object-cover rounded-full"
          onError={() => setProfileImageCandidateIndex((prevIndex) => prevIndex + 1)}
        />
      );
    }
    return username ? username.charAt(0).toUpperCase() : "?";
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  };

  const handleImageClick = () => {
    if (activeProfileImageUrl) {
      setSelectedImage(activeProfileImageUrl);
    }
  };

  useEffect(() => {
    setProfileImageCandidateIndex(0);
  }, [user?._id, profileImageCandidates]);

  const handleCloseModal = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedImage(null);
    }
  };

  const getCountryName = (countryCode) => {
    if (!countryCode) return "Not specified";
    const country = countries.find(c => c.code === countryCode);
    return country ? country.name : countryCode;
  };

  if (loading) {
    return <LoadingSpinner message="Loading your profile..." />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">
            No profile data available
          </h2>
          <button
            onClick={() => navigate(`${INFLUENCER_BASE}/user-details`)}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Complete Your Profile
          </button>
        </div>
      </div>
    );
  }

  const countryName = getCountryName(user.country);
  const locationLabel = [user.city, user.state, countryName !== "Not specified" ? countryName : null]
    .filter(Boolean)
    .join(", ");
  const instagramConnected = Boolean(user.instagramId || user.instagramAccount);
  const facebookConnected = Boolean(user.facebook?.id);
  const connectSocialPath = `${INFLUENCER_BASE}/dashboard/connect-social-media`;

  return (
    <div className="p-4 lg:p-6">
      {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
          >
            <div
              className="relative rounded-xl max-w-2xl w-full aspect-[4/3] p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-4 -right-4 px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-300 transition-all z-10 shadow"
              >
                Close
              </button>
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={selectedImage}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Profile</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage your creator identity, connected platforms, and campaign activity.
            </p>
          </div>
          <button
            onClick={() => navigate(`${INFLUENCER_BASE}/user-details`)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-orange-600"
          >
            <FaEdit />
            Edit Profile
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          <div className="space-y-6 xl:col-span-4">
            <WhiteCard>
              <div className="flex items-start gap-4">
                <button
                  type="button"
                  onClick={handleImageClick}
                  className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-xl font-bold text-gray-700"
                >
                  {getInitial(user?.username)}
                </button>
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-semibold text-gray-900">{user.username}</h2>
                  <p className="truncate text-sm text-gray-500">{user.email}</p>
                  <p className="mt-1 text-sm text-gray-500">{locationLabel || "Location not specified"}</p>
                </div>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Profile status:{" "}
                <span
                  className={`font-medium ${
                    user.isProfileComplete ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {user.isProfileComplete ? "Complete" : "Incomplete"}
                </span>
              </p>
            </WhiteCard>

            <WhiteCard>
              <h3 className="text-base font-semibold text-gray-900">Content Category</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {user.contentType?.length ? (
                  user.contentType.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-orange-50 px-3 py-1 text-sm font-medium text-orange-600"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No content categories added yet.</p>
                )}
              </div>
            </WhiteCard>

            <WhiteCard>
              <h3 className="text-base font-semibold text-gray-900">Social Media</h3>
              <p className="mt-1 text-xs text-gray-500">Links from your connected profiles</p>
              <div className="mt-4 flex items-center gap-3">
                <SocialIcon active={instagramConnected} label="Instagram">
                  <FaInstagram />
                </SocialIcon>
                <SocialIcon active={user.youtubeConnected} label="YouTube">
                  <FaYoutube />
                </SocialIcon>
                <SocialIcon active={facebookConnected} label="Facebook">
                  <FaFacebook />
                </SocialIcon>
              </div>
            </WhiteCard>

            <WhiteCard>
              <h3 className="text-base font-semibold text-gray-900">Personal details</h3>
              <div className="mt-4 space-y-3 text-sm">
                <DetailRow label="Phone Number" value={user.mobile ? formatPhoneNumber(user.mobile) : "Not specified"} />
                <DetailRow label="Gender" value={user.gender || "Not specified"} />
                <DetailRow label="Date of Birth" value={user.dob ? formatDate(user.dob) : "Not specified"} />
                <DetailRow label="Country" value={countryName} />
                <DetailRow label="State" value={user.state || "Not specified"} />
                <DetailRow label="City" value={user.city || "Not specified"} />
                <DetailRow label="Postal Code" value={user.postalCode || "Not specified"} />
              </div>
            </WhiteCard>

            <WhiteCard>
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                <FaCreditCard className="text-orange-500" />
                Your Subscription
              </h3>
              <InfluencerSubscriptionBilling />
            </WhiteCard>
          </div>

          <div className="space-y-6 xl:col-span-8">
            <WhiteCard>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Social metrics</h3>
                  <p className="text-sm text-gray-500">Instagram and YouTube performance overview</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={refreshInsights}
                    disabled={syncing}
                    className={`rounded-lg px-3 py-2 text-sm font-medium text-white ${
                      syncing ? "bg-gray-400" : "bg-orange-500 hover:bg-orange-600"
                    }`}
                  >
                    {syncing ? "Syncing..." : "Sync Insights"}
                  </button>
                  <button
                    onClick={() => navigate(`${INFLUENCER_BASE}/instagram-insights`)}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    View insights
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                <MetricBox label="Instagram Followers" value={user.followersCount || 0} />
                <MetricBox label="Following" value={user.followsCount || 0} />
                <MetricBox label="Media Count" value={user.mediaCount || 0} />
                {user.youtubeConnected ? (
                  <>
                    <MetricBox label="YouTube Subscribers" value={user.youtubeSubscribers || 0} />
                    <MetricBox label="Videos" value={user.youtubeVideoCount || 0} />
                    <MetricBox label="Total Views" value={user.youtubeViewCount || 0} />
                  </>
                ) : null}
              </div>

              {user.instagramAverages ? (
                <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <AverageBlock title="Reels (Avg)" data={user.instagramAverages.reels} />
                  <AverageBlock title="Posts (Avg)" data={user.instagramAverages.posts} />
                </div>
              ) : null}
              {user.instagramAverages?.lastSynced ? (
                <p className="mt-3 text-xs text-gray-400">
                  Last synced: {new Date(user.instagramAverages.lastSynced).toLocaleString()}
                </p>
              ) : null}
            </WhiteCard>

            <WhiteCard>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Platform presence</h3>
                <button
                  type="button"
                  onClick={() => navigate(connectSocialPath)}
                  className="text-sm font-medium text-orange-600 hover:text-orange-700"
                >
                  Manage
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                <PlatformRow
                  icon={<FaInstagram className="text-pink-500" />}
                  name="Instagram"
                  connected={instagramConnected}
                  stats={`${user.mediaCount || 0} posts · ${user.followersCount || 0} followers · ${user.followsCount || 0} following`}
                  highlight={`${user.followersCount || 0} followers`}
                  onAction={() => navigate(connectSocialPath)}
                />
                <PlatformRow
                  icon={<FaYoutube className="text-red-500" />}
                  name={user.youtubeChannelTitle || "YouTube"}
                  connected={Boolean(user.youtubeConnected)}
                  stats={`${user.youtubeVideoCount || 0} videos · ${user.youtubeViewCount || 0} views`}
                  highlight={`${user.youtubeSubscribers || 0} subscribers`}
                  onAction={() => navigate(connectSocialPath)}
                />
                <PlatformRow
                  icon={<FaFacebook className="text-blue-600" />}
                  name={user.facebook?.name || "Facebook"}
                  connected={facebookConnected}
                  stats={facebookConnected ? "Account linked" : "Not connected"}
                  highlight={facebookConnected ? "Connected" : "—"}
                  onAction={() => navigate(connectSocialPath)}
                />
              </div>
            </WhiteCard>

            <WhiteCard>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  <FaDollarSign className="text-green-500" />
                  Service Pricing
                </h3>
                <button
                  onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/service-payment`)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700"
                >
                  <FaEdit />
                  {servicePayment ? "Edit" : "Set pricing"}
                </button>
              </div>

              {!loadingServicePayment && servicePayment ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {servicePayment.youtube?.currency ? (
                    <PricingBlock
                      title="YouTube Pricing"
                      icon={<FaYoutube className="text-red-600" />}
                      currency={servicePayment.youtube.currency}
                      pricing={servicePayment.youtube.pricing}
                      symbol={getCurrentCurrencySymbol("youtube")}
                      labels={{
                        shortVideo: "Short Video",
                        longVideo: "Long Video",
                        liveStream: "Live Stream",
                        collaboration: "Collaboration",
                      }}
                    />
                  ) : null}
                  {servicePayment.instagram?.currency ? (
                    <PricingBlock
                      title="Instagram Pricing"
                      icon={<FaInstagram className="text-pink-500" />}
                      currency={servicePayment.instagram.currency}
                      pricing={servicePayment.instagram.pricing}
                      symbol={getCurrentCurrencySymbol("instagram")}
                      labels={{
                        post: "Post",
                        story: "Story",
                        reel: "Reel",
                        collaboration: "Collaboration",
                      }}
                    />
                  ) : null}
                </div>
              ) : (
                <div className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-500">
                  {loadingServicePayment ? "Loading pricing..." : "No service pricing set yet."}
                </div>
              )}
            </WhiteCard>
          </div>
        </div>
    </div>
  );
};

const WhiteCard = ({ children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">{children}</div>
);

const SocialIcon = ({ active, label, children }) => (
  <div
    title={active ? `${label} connected` : `${label} not connected`}
    className={`relative flex h-11 w-11 items-center justify-center rounded-xl border text-lg ${
      active
        ? "border-gray-200 bg-white text-gray-800"
        : "border-gray-200 bg-white text-gray-300"
    }`}
  >
    {children}
    {active ? (
      <span className="absolute right-0 top-0 flex h-2 w-2 items-center justify-center rounded-full bg-green-500 text-white shadow-sm">
        <FaCheck className="h-1 w-1" />
      </span>
    ) : null}
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium capitalize text-gray-900">{value}</span>
  </div>
);

const MetricBox = ({ label, value }) => (
  <div className="rounded-xl bg-gray-50 p-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-semibold text-gray-900">
      {typeof value === "number" ? value.toLocaleString() : value}
    </p>
  </div>
);

const AverageBlock = ({ title, data = {} }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
    <h4 className="mb-2 text-sm font-semibold text-gray-800">{title}</h4>
    <div className="space-y-1 text-sm text-gray-600">
      {Object.entries(data).map(([key, val]) => (
        <div key={key} className="flex justify-between">
          <span className="capitalize">{key.replace(/_/g, " ")}</span>
          <span className="font-medium text-gray-900">{val}</span>
        </div>
      ))}
    </div>
  </div>
);

const PlatformRow = ({ icon, name, connected, stats, highlight, onAction }) => (
  <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 items-start gap-3">
      <div className="mt-0.5 text-xl">{icon}</div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{name}</span>
          <span className={`text-xs font-medium ${connected ? "text-green-600" : "text-orange-500"}`}>
            {connected ? "Connected" : "Not connected"}
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-500">{stats}</p>
      </div>
    </div>
    <div className="flex items-center justify-between gap-4 sm:justify-end">
      <span className="text-sm font-semibold text-gray-900">{highlight}</span>
      <button
        type="button"
        onClick={onAction}
        className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
          connected
            ? "border border-gray-200 text-gray-700 hover:bg-gray-50"
            : "bg-orange-500 text-white hover:bg-orange-600"
        }`}
      >
        {connected ? "Manage" : "Connect"}
      </button>
    </div>
  </div>
);

const PricingBlock = ({ title, icon, currency, pricing = {}, symbol, labels }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
    <div className="mb-3 flex items-center gap-2">
      <span className="text-xl">{icon}</span>
      <h4 className="font-semibold text-gray-800">{title}</h4>
    </div>
    <p className="mb-2 text-xs text-gray-500">Currency: {currency}</p>
    <div className="space-y-2">
      {Object.entries(pricing)
        .filter(([, price]) => price && price > 0)
        .map(([service, price]) => (
          <div key={service} className="flex justify-between text-sm">
            <span className="text-gray-700">{labels[service] || service}</span>
            <span className="font-semibold text-gray-900">
              {symbol}
              {parseFloat(price).toFixed(2)}
            </span>
          </div>
        ))}
    </div>
  </div>
);

export default InfluancerProfile;
